import { createWebHistory, createRouter, onBeforeRouteLeave } from 'vue-router';
import { computed } from 'vue';
import NotFound from '@views/NotFoundView.vue';
import LoginView from '@views/Auth/LoginView.vue';
import LogoutView from '@views/Auth/LogoutView.vue';
import DashboardView from '@views/Dashboard';
import Sessions from '@views/Sessions';
import SessionUsers from '@views/Sessions/views/SessionUsers.vue';
import UserPage from '@views/UserPage/UserPage.vue';
import Result from '@views/Result.vue';
import apiKeys from '@views/ApiKeys';
import userCreate from '@views/userCreate';
import UserProfile from '@views/UserProfile';
import { useAuthStore, useSessionStore } from '@stores';
import i18n from '../locales/i18n';
import AppLayout from '@layout/AppLayout.vue';
import { useTestsStore } from '@stores';

import TestConstructor from '@views/TestConstructor';
import TestLayout from '@views/TestConstructor/views/TestLayout';
import Questions from '@views/TestConstructor/views/Questions';
import Main from '@views/TestConstructor/views/Main';
import Preview from '@views/TestConstructor/views/Preview';
import TestRender from '@views/TestRender';
import TestResult from '@views/TestRender/views/Result';

const { t } = i18n.global;

const routes = [
    {
        path: '/',
        component: AppLayout,
        children: [
            {
                path: '/',
                name: t('global.review'),
                component: DashboardView,
                meta: {
                    requiresAuth: true,
                    // breadCrumb() {
                    //     return [{ name: t('global.review'), path: '/' }];
                    // },
                },
            },
            {
                path: '/sessions',
                name: t('global.sessions'),
                component: Sessions,
                meta: {
                    requiresAuth: true,
                    breadCrumb() {
                        return [{ label: t('global.sessions'), route: '/sessions' }];
                    },
                },
            },
            {
                path: '/session/:sessionId',
                name: t('global.sessionUser'),
                component: SessionUsers,
                props: true,
                meta: {
                    requiresAuth: true,
                    breadCrumb(route) {
                        return [
                            { label: t('global.sessions'), route: '/sessions' },
                            {
                                label: t('global.sessionUser'),
                                route: `/session/${route.params.sessionId}`,
                            },
                        ];
                    },
                },
            },
            {
                path: '/session/:sessionId/user/:userId',
                name: t('profile.examinee'),
                props: true,
                component: UserPage,
                meta: {
                    requiresAuth: true,
                    breadCrumb({ params }) {
                        return [
                            { label: t('global.sessions'), route: '/sessions' },
                            {
                                label: t('global.sessionUser'),
                                route: `/session/${params.sessionId}`,
                            },
                            {
                                label: t('profile.examinee'),
                                route: `/session/${params.sessionId}/user/${params.userId}`,
                            },
                        ];
                    },
                },
            },
            {
                path: '/apikeys',
                name: t('apikey.apiKeys'),
                component: apiKeys,
                meta: {
                    requiresAuth: true,
                    restrictedRoles: ['Moderator'],
                    breadCrumb() {
                        return [{ label: t('apikey.apiKeys'), route: '/apikeys' }];
                    },
                },
            },
            {
                path: '/user_create',
                name: t('userCreate.title'),
                component: userCreate,
                meta: {
                    requiresAuth: true,
                    restrictedRoles: ['Moderator'],
                    breadCrumb() {
                        return [{ label: t('userCreate.title'), route: '/user_create' }];
                    },
                },
            },
            {
                path: '/profile',
                name: t('profile.userProfile'),
                component: UserProfile,
                meta: {
                    requiresAuth: true,
                    breadCrumb() {
                        return [{ label: t('profile.userProfile'), route: '/profile' }];
                    },
                },
            },
            {
                path: '/tests',
                name: t('tests.title'),
                component: TestConstructor,
                meta: {
                    restrictedRoles: ['Superadmin', 'Moderator'],
                    requiresAuth: true,
                    breadCrumb() {
                        return [{ label: t('tests.title'), route: '/tests' }];
                    },
                },
            },
            {
                path: '/tests/:testId',
                redirect: (to) => `/tests/${to.params.testId}/main`,
                component: TestLayout,
                children: [
                    { path: 'questions', component: Questions, meta: { title: computed(() => t('tests.tabs.questions')) } },
                    { path: 'main', component: Main, meta: { title: computed(() => t('tests.tabs.main')) } },
                    { path: 'preview', component: Preview, meta: { title: computed(() => t('tests.tabs.preview')) } },
                ],
                meta: {
                    restrictedRoles: ['Superadmin', 'Moderator'],
                    requiresAuth: true,
                    breadCrumb(route) {
                        const testsStore = useTestsStore();
                        return [
                            { label: t('tests.title'), route: '/tests' },
                            { label: testsStore.main.name, route: `/tests/${route.params.testId}` },
                            { label: `${route.meta.title.value}`, route: `${route.path}` },
                        ];
                    },
                },
            },
        ],
        beforeEnter: async (to, from, next) => {
            const sessionStore = useSessionStore();
            try {
                await sessionStore.getSessions();
                next();
            } catch (e) {
                router.push('/logout');
            }
        },
    },
    {
        path: '/result-exam/:attemptId',
        name: Result,
        component: Result,
    },
    {
        path: '/testing',
        name: TestRender,
        component: TestRender,
    },
    {
        path: '/result',
        name: TestResult,
        component: TestResult,
    },
    {
        path: '/auth',
        name: 'Auth',
        component: LoginView,
    },

    {
        path: '/logout',
        name: 'Logout',
        component: LogoutView,
        meta: { requiresAuth: true },
    },
    {
        path: '/:catchAll(.*)',
        component: NotFound,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active-router-link',
});

router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore();

    // Если требуется авторизация, но пользователь не авторизован
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        // Проверяем, есть ли токен в localStorage
        const userToken = localStorage.getItem('userToken');
        const userData = localStorage.getItem('user');
        
        if (userToken && userData) {
            try {
                // Попробуем восстановить авторизацию
                const { jwtDecode } = await import('jwt-decode');
                const decoded = jwtDecode(userToken);
                
                // Проверяем, не истек ли токен
                const isExpired = decoded.exp * 1000 < Date.now();
                if (!isExpired) {
                    const parsedUser = JSON.parse(userData);
                    if (parsedUser.image && parsedUser.full_name) {
                        // Восстанавливаем авторизацию
                        authStore.setAuthData(userToken, parsedUser.image, parsedUser.full_name);
                        
                        // Проверяем роли после восстановления авторизации
                        const userRoles = authStore.user?.roles?.split(',').map((r) => r.trim()) || [];
                        if (to.meta.restrictedRoles && to.meta.restrictedRoles.some((role) => userRoles.includes(role))) {
                            next('/');
                        } else {
                            next();
                        }
                        return;
                    }
                }
            } catch (error) {
                console.error('Error restoring auth in router guard:', error);
                // Очищаем невалидные данные
                localStorage.removeItem('userToken');
                localStorage.removeItem('user');
            }
        }
        
        // Если восстановление не удалось, перенаправляем на авторизацию
        next('/auth');
    } else {
        // Проверяем роли для авторизованных пользователей
        const userRoles = authStore.user?.roles?.split(',').map((r) => r.trim()) || [];
        if (to.meta.restrictedRoles && to.meta.restrictedRoles.some((role) => userRoles.includes(role))) {
            next('/');
        } else {
            next();
        }
    }
});
export { router };
